import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  useTheme,
  Paper,
  useMediaQuery,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  alpha,
  Alert,
  Avatar,
  Tooltip,
  Grid,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBack,
  Search,
  Clear,
  History,
  Report,
  Person,
  CheckCircle,
  FilterList,
  Warning,
  Delete,
  Refresh,
  Coffee,
  CalendarToday,
  Visibility,
  Group,
  PostAdd,
} from '@mui/icons-material';
import AdminReportsActionModal from '../components/Admin/AdminPostReportsActionModal';
import AdminCommunityReportsActionModal from '../components/Admin/AdminCommunityReportActionModal';
import heroImage from '../assets/reports-WObg.png';
import { AdminTableSkeleton, SCircle } from '../components/skeletons';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

interface ReportData {
  _id: string;
  postId?: string;
  communityId?: string;
  postTitle?: string;
  communityName?: string;
  communitySlug?: string;
  postAuthorId?: string;
  postAuthorUsername?: string;
  communityOwnerId?: string;
  communityOwnerUsername?: string;
  reportType: string;
  customReason?: string;
  description?: string;
  reporterId: string;
  reporterUsername: string;
  reporterEmail: string;
  timestamp: string;
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  createdAt: string;
  updatedAt: string;
  targetType?: 'post' | 'community';
  // Community specific fields
  communityDescription?: string;
  communityProfilePicture?: string;
  communityBannerImage?: string;
  memberCountAtReport?: number;
  postCountAtReport?: number;
}

interface UserProfile {
  id: string;
  username: string;
  profilePicture?: string;
}

interface PaginatedResponse {
  success: boolean;
  reports: ReportData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  stats?: {
    total: number;
    pending: number;
    reviewed: number;
    resolved: number;
    dismissed: number;
  };
}

interface CommunityReportResponse {
  success: boolean;
  reports: ReportData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
  stats?: {
    total: number;
    pending: number;
    reviewed: number;
    resolved: number;
    dismissed: number;
  };
}

const AdminReports: React.FC = () => {
  const [reportsList, setReportsList] = useState<ReportData[]>([]);
  const [filteredReports, setFilteredReports] = useState<ReportData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'pending' | 'reviewed' | 'resolved' | 'dismissed'>('all');
  const [reportTypeFilter, setReportTypeFilter] = useState<'all' | 'post' | 'community'>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedReport, setSelectedReport] = useState<ReportData | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    reviewed: 0,
    resolved: 0,
    dismissed: 0,
  });
  const [userProfiles, setUserProfiles] = useState<Record<string, UserProfile>>({});
  const [profileLoading, setProfileLoading] = useState<Record<string, boolean>>({});

  const { user } = useAuth();
  const theme = useTheme();
  const { mode } = useCustomTheme();
  const navigate = useNavigate();
  
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const discordFont = {
    fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
    fontWeight: 800,
    letterSpacing: '-0.02em',
  };

  const glassEffect = {
    background: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(20px)',
    border: isDark 
      ? '1px solid rgba(196, 182, 111, 0.2)' 
      : '1px solid rgba(5, 24, 20, 0.1)',
    boxShadow: isDark 
      ? '0 20px 60px rgba(0,0,0,0.7)' 
      : '0 20px 60px rgba(5, 24, 20, 0.1)',
  };

  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';

  useEffect(() => {
    if (user) {
      fetchReports();
      fetchStats();
    }
  }, [user, page, rowsPerPage, activeTab, searchTerm, reportTypeFilter]);

  useEffect(() => {
    filterAndSortReports();
  }, [reportsList]);

  useEffect(() => {
    const fetchUserProfiles = async () => {
      const reportersWithId = reportsList.filter(r => r.reporterId);
      const uniqueUserIds = Array.from(new Set(reportersWithId.map(r => r.reporterId)));
      
      for (const userId of uniqueUserIds) {
        if (userId && !userProfiles[userId] && !profileLoading[userId]) {
          try {
            setProfileLoading(prev => ({ ...prev, [userId]: true }));
            const token = localStorage.getItem('token');
            
            let userData = null;
            
            // Try endpoint 1: /api/user/:id
            try {
              const response = await fetch(`${API_URL}/api/user/${userId}`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              });
              
              if (response.ok) {
                const data = await response.json();
                if (data.success && data.user) {
                  userData = data.user;
                }
              }
            } catch (err) {
              console.log(`Endpoint /api/user/${userId} failed, trying alternatives`);
            }
            
            // Try endpoint 2: /api/users/:id
            if (!userData) {
              try {
                const response = await fetch(`${API_URL}/api/users/${userId}`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                });
                
                if (response.ok) {
                  const data = await response.json();
                  if (data.success && data.user) {
                    userData = data.user;
                  }
                }
              } catch (err) {
                console.log(`Endpoint /api/users/${userId} failed`);
              }
            }
            
            // Try endpoint 3: Get from all users and filter
            if (!userData) {
              try {
                const response = await fetch(`${API_URL}/api/user/all`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                  },
                });
                
                if (response.ok) {
                  const data = await response.json();
                  if (data.success && data.users) {
                    const foundUser = data.users.find((u: any) => u._id === userId);
                    if (foundUser) {
                      userData = foundUser;
                    }
                  }
                }
              } catch (err) {
                console.log('Failed to fetch all users');
              }
            }
            
            if (userData) {
              setUserProfiles(prev => ({
                ...prev,
                [userId]: {
                  id: userData._id,
                  username: userData.username,
                  profilePicture: userData.profilePicture
                }
              }));
            } else {
              console.warn(`Could not find user with ID: ${userId}`);
            }
            
          } catch (err) {
            console.error(`Error fetching profile for user ${userId}:`, err);
          } finally {
            setProfileLoading(prev => ({ ...prev, [userId]: false }));
          }
        }
      }
    };
    
    if (reportsList.length > 0) {
      fetchUserProfiles();
    }
  }, [reportsList]);

  const filterAndSortReports = () => {
    let result = [...reportsList];
    
    // Filter by report type (post/community)
    if (reportTypeFilter !== 'all') {
      result = result.filter(report => {
        if (reportTypeFilter === 'post') {
          return report.postId && !report.communityId;
        } else if (reportTypeFilter === 'community') {
          return report.communityId && !report.postId;
        }
        return true;
      });
    }
    
    // Sort by recent
    result.sort((a, b) => {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return dateB.getTime() - dateA.getTime();
    });
    
    setFilteredReports(result);
  };

  const fetchReports = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      let postReports: ReportData[] = [];
      let communityReports: ReportData[] = [];
      let totalPostReports = 0;
      let totalCommunityReports = 0;

      // Fetch post reports
      const postParams = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        ...(activeTab !== 'all' && { status: activeTab }),
        ...(searchTerm && { search: searchTerm }),
      });

      const postResponse = await fetch(`${API_URL}/api/reports?${postParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (postResponse.ok) {
        const data: PaginatedResponse = await postResponse.json();
        if (data.success) {
          postReports = data.reports.map(report => ({
            ...report,
            targetType: 'post'
          }));
          totalPostReports = data.pagination.total;
        }
      }

      // Fetch community reports
      const communityParams = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        ...(activeTab !== 'all' && { status: activeTab }),
      });

      const communityResponse = await fetch(`${API_URL}/api/report-community/community/admin?${communityParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (communityResponse.ok) {
        const data: CommunityReportResponse = await communityResponse.json();
        if (data.success) {
          communityReports = data.reports.map(report => ({
            ...report,
            targetType: 'community'
          }));
          totalCommunityReports = data.pagination.total;
        }
      }

      // Combine both reports
      const allReports = [...postReports, ...communityReports];
      setReportsList(allReports);
      setTotal(totalPostReports + totalCommunityReports);
      
    } catch (err: any) {
      console.error('Error fetching reports:', err);
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch post report stats
      const postResponse = await fetch(`${API_URL}/api/reports/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      // Fetch community report stats
      const communityResponse = await fetch(`${API_URL}/api/report-community/community/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      let postStats = { total: 0, pending: 0, reviewed: 0, resolved: 0, dismissed: 0 };
      let communityStats = { total: 0, pending: 0, reviewed: 0, resolved: 0, dismissed: 0 };

      if (postResponse.ok) {
        const data = await postResponse.json();
        if (data.success && data.stats) {
          postStats = data.stats;
        }
      }

      if (communityResponse.ok) {
        const data = await communityResponse.json();
        if (data.success && data.stats) {
          communityStats = data.stats;
        }
      }

      // Combine stats
      setStats({
        total: postStats.total + communityStats.total,
        pending: postStats.pending + communityStats.pending,
        reviewed: postStats.reviewed + communityStats.reviewed,
        resolved: postStats.resolved + communityStats.resolved,
        dismissed: postStats.dismissed + communityStats.dismissed,
      });

    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
      
      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
      
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const handleClearSearch = () => setSearchTerm('');
  
  const handleStatusUpdate = async (reportId: string, newStatus: 'pending' | 'reviewed' | 'resolved' | 'dismissed') => {
    try {
      const token = localStorage.getItem('token');
      
      // Determine which endpoint to use based on report type
      const report = reportsList.find(r => r._id === reportId);
      const isCommunityReport = report?.targetType === 'community';
      
      const endpoint = isCommunityReport 
          ? `${API_URL}/api/report-community/community/admin/${reportId}/status`
          : `${API_URL}/api/reports/${reportId}/status`;

      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (response.ok) {
        fetchReports();
        fetchStats();
        setModalOpen(false);
        setSelectedReport(null);
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const handleDeletePost = async (postId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchReports();
        fetchStats();
        setModalOpen(false);
        setSelectedReport(null);
      }
    } catch (err) {
      console.error('Error deleting post:', err);
    }
  };

  const handleDeleteCommunity = async (communityId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/community/${communityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchReports();
        fetchStats();
        setModalOpen(false);
        setSelectedReport(null);
      }
    } catch (err) {
      console.error('Error deleting community:', err);
    }
  };

  const getInitials = (username: string) => {
    return username
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Use theme colors for status
  const getStatusColor = (status: string) => {
    return isDark ? goldColor : deepGreen;
  };

  // Use theme colors for report types
  const getReportTypeColor = (reportType: string) => {
    return isDark ? goldColor : deepGreen;
  };

  const getReportTypeLabel = (reportType: string) => {
    const types: Record<string, string> = {
      'spam': 'Spam',
      'inappropriate': 'Inappropriate',
      'harassment': 'Harassment',
      'hate_speech': 'Hate Speech',
      'misinformation': 'Misinformation',
      'violence': 'Violence',
      'copyright': 'Copyright',
      'other': 'Other',
      'custom': 'Custom',
      'inappropriate_content': 'Inappropriate Content',
      'community_guidelines': 'Community Guidelines',
      'privacy_violation': 'Privacy Violation',
      'vulgar_content': 'Vulgar Content',
    };
    return types[reportType] || reportType.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getTargetTypeLabel = (targetType?: 'post' | 'community') => {
    if (!targetType) return 'Unknown';
    return targetType === 'community' ? 'Community' : 'Post';
  };

  const handleRowClick = (report: ReportData) => {
    setSelectedReport(report);
    setModalOpen(true);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewPost = (postId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/post/${postId}`);
  };

  const handleViewCommunity = (communitySlug: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/community/${communitySlug}`);
  };

  const getTargetLabel = (targetType: 'post' | 'community' | undefined) => {
    return targetType === 'community' ? 'Community' : 'Post';
  };

  const getTargetIcon = (targetType?: 'post' | 'community') => {
    if (targetType === 'community') {
      return <Group sx={{ fontSize: 14, mr: 0.5 }} />;
    }
    return <PostAdd sx={{ fontSize: 14, mr: 0.5 }} />;
  };

  const MobileStatCard = ({ title, value, icon, label, isFirstInRow }: any) => (
    <Grid size={{ xs: 6, sm: 3 }}>
      <Paper
        elevation={0}
        sx={{
          ...glassEffect,
          p: 2,
          borderRadius: '12px',
          mb: 2,
          ml: isFirstInRow ? 0 : 1,
          mr: isFirstInRow ? 1 : 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              bgcolor: alpha(goldColor, 0.1),
              borderRadius: '10px',
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 40,
            }}
          >
            {React.cloneElement(icon, { 
              sx: { 
                color: goldColor,
                fontSize: 20
              } 
            })}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
                fontSize: '0.7rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: isDark ? '#fff' : deepGreen,
                fontWeight: 800,
                fontSize: '1.25rem',
                lineHeight: 1,
                mt: 0.5,
              }}
            >
              {value}
            </Typography>
            {label && (
              <Typography
                variant="caption"
                sx={{
                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)',
                  fontSize: '0.65rem',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {label}
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>
    </Grid>
  );

  const DesktopStatCard = ({ title, value, icon, label }: any) => (
    <Paper
      elevation={0}
      sx={{
        ...glassEffect,
        p: { xs: 2, md: 3 },
        borderRadius: '16px',
        flex: 1,
        minWidth: { xs: '100%', sm: '45%', md: 'auto' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          sx={{
            bgcolor: alpha(goldColor, 0.1),
            borderRadius: '12px',
            p: { xs: 1, md: 1.5 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(icon, { 
            sx: { 
              color: goldColor,
              fontSize: { xs: 24, md: 32 }
            } 
          })}
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
              fontSize: '0.875rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              color: isDark ? '#fff' : deepGreen,
              fontWeight: 800,
              fontSize: { xs: '1.5rem', md: '2rem' },
              lineHeight: 1,
            }}
          >
            {value}
          </Typography>
          {label && (
            <Typography
              variant="caption"
              sx={{
                color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)',
                fontSize: '0.75rem',
              }}
            >
              {label}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );

  const statsData = [
    {
      title: 'Total',
      value: stats.total,
      icon: <Report />,
      label: 'All Reports',
    },
    {
      title: 'Pending',
      value: stats.pending,
      icon: <Warning />,
      label: 'Requires Action',
    },
    {
      title: 'Reviewed',
      value: stats.reviewed,
      icon: <FilterList />,
      label: 'In Review',
    },
    {
      title: 'Resolved',
      value: stats.resolved,
      icon: <CheckCircle />,
      label: 'Action Taken',
    },
  ];

  if (!user) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: isDark ? '#000000' : '#FAFAFA', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Container maxWidth="sm">
          <Paper sx={{ p: 6, borderRadius: '32px', textAlign: 'center', ...glassEffect }}>
            <Coffee sx={{ fontSize: 80, color: goldColor, mb: 3 }} />
            <Typography variant="h4" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, mb: 2 }}>Admin Access Required</Typography>
            <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mb: 4 }}>You need to be logged in as an admin to access this panel.</Typography>
            <Button variant="contained" onClick={() => navigate('/login')} sx={{ background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814', color: isDark ? '#051814' : '#FFFFFF', borderRadius: '16px', px: 4, py: 1.5, fontWeight: 700, '&:hover': { background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E' } }}>Go to Login</Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: isDark ? '#000000' : '#FAFAFA',
      overflowX: 'hidden',
    }}>
      <Container maxWidth="xl" sx={{ 
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
      }}>
        {/* Back button that goes to previous page */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)} 
          sx={{ 
            mb: 3, 
            color: goldColor, 
            '&:hover': { background: isDark ? 'rgba(196, 182, 111, 0.1)' : 'rgba(196, 182, 111, 0.05)' } 
          }}
        >
          Back
        </Button>

        <Paper sx={{ 
          p: 0, 
          mb: 6, 
          borderRadius: '32px', 
          overflow: 'hidden', 
          ...glassEffect, 
          display: 'flex', 
          flexDirection: { xs: 'column-reverse', md: 'row' }, 
          alignItems: 'center',
          textAlign: { xs: 'center', md: 'left' },
        }}>
          <Box sx={{ 
            p: { xs: 3, md: 6 }, 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'center', md: 'flex-start' },
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2,
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}>
                <Report sx={{ fontSize: { xs: 32, md: 40 }, color: goldColor, mr: 2 }} />
                <Typography variant="h3" sx={{ 
                  ...discordFont, 
                  background: isDark ? 'linear-gradient(135deg, #C4B66F 0%, #FFFFFF 100%)' : `linear-gradient(135deg, ${deepGreen} 0%, ${deepGreen} 100%)`, 
                  backgroundClip: 'text', 
                  WebkitBackgroundClip: 'text', 
                  color: 'transparent', 
                  fontSize: { xs: '1.75rem', md: '3rem' },
                  textAlign: { xs: 'center', md: 'left' },
                }}>
                    Reports Admin
                </Typography>
            </Box>
            <Typography variant="h6" sx={{ 
              color: isDark ? 'rgba(255, 255, 255, 0.8)' : 'rgba(5, 24, 20, 0.8)', 
              fontWeight: 400, 
              maxWidth: 600,
              textAlign: { xs: 'center', md: 'left' },
              fontSize: { xs: '1rem', md: '1.25rem' },
            }}>
              Manage user reports on posts and communities. Review reported content, take action against inappropriate content, and maintain community guidelines.
            </Typography>
          </Box>
          <Box sx={{ 
            width: { xs: '100%', md: '40%' }, 
            height: { xs: 180, md: '100%' }, 
            minHeight: { md: 300 }, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            p: { xs: 2, md: 4 } 
          }}>
             <img src={heroImage} alt="Admin Hero" style={{ 
               width: '100%', 
               height: '100%', 
               objectFit: 'contain', 
               maxHeight: 250 
             }} />
          </Box>
        </Paper>

        {isMobile ? (
          <Grid container spacing={0} sx={{ mb: 3, width: '100%', mx: 0 }}>
            {statsData.map((stat, index) => (
              <MobileStatCard 
                key={index} 
                {...stat} 
                isFirstInRow={index % 2 === 0}
              />
            ))}
          </Grid>
        ) : (
          <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, mb: 4, flexWrap: 'wrap' }}>
            {statsData.map((stat, index) => (
              <DesktopStatCard key={index} {...stat} />
            ))}
          </Box>
        )}

        <Paper sx={{ 
          p: 3, 
          mb: 4, 
          borderRadius: '20px', 
          ...glassEffect,
          overflow: 'hidden',
        }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search reports..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                maxWidth: { xs: '100%', md: 1050 },

                '& .MuiOutlinedInput-root': {
                  borderRadius: '22px',
                  background: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.05)',
                  '& fieldset': {
                    borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: goldColor,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: goldColor,
                  },
                },
                '& .MuiInputBase-input': {
                  color: isDark ? '#FFFFFF' : deepGreen,
                  fontSize: { xs: '0.95rem', md: '1rem' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: goldColor, opacity: 0.7 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <Clear sx={{ fontSize: 18, color: goldColor }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Report Type Filter - Pill shaped buttons */}
            <Box sx={{ 
              display: 'flex', 
              gap: 1, 
              flexWrap: 'wrap',
              alignItems: 'center',
            }}>
              {['all', 'post', 'community'].map((type) => (
                <Button
                  key={type}
                  variant={reportTypeFilter === type ? "contained" : "outlined"}
                  onClick={() => setReportTypeFilter(type as any)}
                  size={isMobile ? "small" : "medium"}
                  sx={{
                    borderRadius: '50px',
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                    px: { xs: 2, sm: 3 },
                    py: { xs: 0.5, sm: 0.75 },
                    minWidth: { xs: 'auto', sm: '100px' },
                    ...(reportTypeFilter === type ? {
                      background: isDark ? goldColor : deepGreen,
                      color: isDark ? deepGreen : '#FFFFFF',
                      '&:hover': {
                        background: isDark ? alpha(goldColor, 0.9) : alpha(deepGreen, 0.9),
                      }
                    } : {
                      borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.3)',
                      color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
                      '&:hover': {
                        borderColor: goldColor,
                        color: goldColor,
                        backgroundColor: alpha(goldColor, 0.05),
                      }
                    })
                  }}
                >
                  {type === 'all' ? 'All' : type === 'post' ? 'Posts' : 'Communities'}
                </Button>
              ))}
            </Box>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' }, 
            gap: 2, 
            justifyContent: 'space-between', 
            alignItems: { sm: 'center' },
            overflow: 'hidden',
          }}>
            <Box sx={{ 
              width: '100%', 
              overflowX: 'auto',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              scrollbarWidth: 'none',
            }}>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant={isMobile ? "scrollable" : "standard"}
                scrollButtons={isMobile ? "auto" : false}
                sx={{
                  '& .MuiTab-root': {
                    color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
                    fontWeight: 600,
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: { xs: 1, sm: 2 },
                    fontSize: { xs: '0.85rem', sm: '0.875rem' },
                    '&.Mui-selected': {
                      color: goldColor,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: goldColor,
                    borderRadius: '3px',
                  },
                }}
              >
                <Tab label={`All (${reportsList.length})`} value="all" />
                <Tab label={`Pending (${stats.pending})`} value="pending" />
                <Tab label={`Reviewed (${stats.reviewed})`} value="reviewed" />
                <Tab label={`Resolved (${stats.resolved})`} value="resolved" />
                <Tab label={`Dismissed (${stats.dismissed})`} value="dismissed" />
              </Tabs>
            </Box>
          </Box>
        </Paper>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: '12px',
              ...glassEffect,
            }}
            action={
              <Button color="inherit" size="small" onClick={fetchReports}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        )}

        <Paper sx={{ 
          p: { xs: 2, sm: 3 }, 
          borderRadius: '20px', 
          ...glassEffect,
          overflow: 'hidden',
        }}>
          {loading ? (
            <AdminTableSkeleton rows={8} cols={5} />
          ) : filteredReports.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Report sx={{ fontSize: 80, color: goldColor, mb: 3, opacity: 0.5 }} />
              <Typography variant="h4" sx={{ ...discordFont, color: isDark ? '#FFFFFF' : deepGreen, mb: 2 }}>{searchTerm ? 'No Matching Reports' : 'No Reports Yet'}</Typography>
              <Typography sx={{ color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)', mb: 4, maxWidth: 400, mx: 'auto' }}>{searchTerm ? `No reports found matching "${searchTerm}".` : "No reports have been submitted yet."}</Typography>
              <Button variant="contained" onClick={() => searchTerm ? setSearchTerm('') : fetchReports} sx={{ background: isDark ? 'rgba(196, 182, 111, 0.9)' : '#051814', color: isDark ? '#051814' : '#FFFFFF', borderRadius: '16px', px: 4, py: 1.5, fontWeight: 700, '&:hover': { background: isDark ? 'rgba(196, 182, 111, 1)' : '#0F3B2E' } }}>{searchTerm ? 'Clear Search' : 'Refresh'}</Button>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ 
                overflowX: 'auto',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                scrollbarWidth: 'none',
              }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow sx={{ 
                      '& th': { 
                        borderBottom: `2px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
                        color: isDark ? goldColor : deepGreen,
                        fontWeight: 700,
                        fontSize: { xs: '0.8rem', sm: '0.875rem', md: '1rem' },
                        py: { xs: 1, sm: 2 },
                        px: { xs: 1, sm: 2 },
                        whiteSpace: 'nowrap',
                      }
                    }}>
                      <TableCell>Reported Content</TableCell>
                      <TableCell>Reporter</TableCell>
                      <TableCell>Report Type</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredReports.map((report) => {
                      const reporterProfile = report.reporterId ? userProfiles[report.reporterId] : null;
                      const isLoadingProfile = report.reporterId ? profileLoading[report.reporterId] : false;
                      const isCommunityReport = report.targetType === 'community';
                      
                      return (
                        <TableRow 
                          key={report._id}
                          hover
                          onClick={() => handleRowClick(report)}
                          sx={{ 
                            cursor: 'pointer',
                            borderRadius: '12px',
                            '&:hover': { 
                              backgroundColor: alpha(isDark ? goldColor : deepGreen, isDark ? 0.05 : 0.02),
                              '& td:first-of-type': {
                                borderTopLeftRadius: '12px',
                                borderBottomLeftRadius: '12px',
                              },
                              '& td:last-of-type': {
                                borderTopRightRadius: '12px',
                                borderBottomRightRadius: '12px',
                              },
                            },
                            '& td': {
                              borderBottom: `1px solid ${alpha(isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)', 0.3)}`,
                              py: { xs: 1.5, sm: 2 },
                              px: { xs: 1, sm: 2 },
                              transition: 'background-color 0.2s ease',
                            },
                          }}
                        >
                          <TableCell sx={{ maxWidth: { xs: 120, sm: 200 } }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Chip
                                  size="small"
                                  label={getTargetTypeLabel(report.targetType)}
                                  icon={getTargetIcon(report.targetType)}
                                  sx={{
                                    bgcolor: alpha(goldColor, isDark ? 0.15 : 0.1),
                                    color: goldColor,
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    height: 24,
                                    '& .MuiChip-icon': {
                                      color: goldColor,
                                      fontSize: 12,
                                      marginLeft: '4px',
                                    }
                                  }}
                                />
                              </Box>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: isDark ? '#fff' : deepGreen,
                                  fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {isCommunityReport ? report.communityName : report.postTitle}
                              </Typography>
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)',
                                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {isCommunityReport 
                                  ? `Owner: ${report.communityOwnerUsername || 'Unknown'}`
                                  : `Author: ${report.postAuthorUsername || 'Unknown'}`
                                }
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                              {isLoadingProfile ? (
                                <SCircle size={32} />
                              ) : (
                                <Avatar
                                  src={reporterProfile?.profilePicture}
                                  sx={{
                                    bgcolor: alpha(goldColor, 0.1),
                                    color: goldColor,
                                    fontWeight: 700,
                                    width: { xs: 40, sm: 48 },
                                    height: { xs: 40, sm: 48 },
                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                  }}
                                >
                                  {getInitials(report.reporterUsername)}
                                </Avatar>
                              )}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body1" sx={{ 
                                  fontWeight: 600,
                                  color: isDark ? '#fff' : deepGreen,
                                  fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {report.reporterUsername}
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                  color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(5,24,20,0.6)',
                                  fontSize: { xs: '0.75rem', sm: '0.9rem' },
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}>
                                  {report.reporterEmail}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getReportTypeLabel(report.reportType)}
                              size={isMobile ? "small" : "medium"}
                              sx={{
                                bgcolor: alpha(getReportTypeColor(report.reportType), isDark ? 0.15 : 0.1),
                                color: getReportTypeColor(report.reportType),
                                fontWeight: 600,
                                fontSize: { xs: '0.75rem', sm: '0.9rem' },
                                border: `1px solid ${alpha(getReportTypeColor(report.reportType), 0.3)}`,
                                height: { xs: 24, sm: 32 },
                                maxWidth: '140px',
                                '& .MuiChip-label': {
                                  px: 1,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CalendarToday sx={{ 
                                fontSize: { xs: 16, sm: 20 }, 
                                color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)' 
                              }} />
                              <Typography variant="body1" sx={{ 
                                color: isDark ? 'rgba(255,255,255,0.9)' : 'rgba(5,24,20,0.9)',
                                fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                              }}>
                                {formatDate(report.timestamp)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={report.status}
                              size={isMobile ? "small" : "medium"}
                              sx={{
                                bgcolor: alpha(getStatusColor(report.status), isDark ? 0.15 : 0.1),
                                color: getStatusColor(report.status),
                                fontWeight: 700,
                                fontSize: { xs: '0.8rem', sm: '0.95rem' },
                                textTransform: 'capitalize',
                                border: `2px solid ${alpha(getStatusColor(report.status), 0.3)}`,
                                height: { xs: 28, sm: 36 },
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={(e) => isCommunityReport 
                                ? handleViewCommunity(report.communitySlug || '', e)
                                : handleViewPost(report.postId || '', e)
                              }
                              startIcon={<Visibility />}
                              sx={{
                                border: `1px solid ${goldColor}`,
                                color: goldColor,
                                '&:hover': {
                                  border: `1px solid ${goldColor}`,
                                  backgroundColor: alpha(goldColor, 0.1),
                                },
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                py: 0.5,
                                px: 1.5,
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: `1px solid ${alpha(
                    isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
                    0.3
                  )}`,
                  mt: 2,
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    color: isDark
                      ? 'rgba(255,255,255,0.7)'
                      : 'rgba(5,24,20,0.7)',
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                    display: { xs: 'none', sm: 'inline-flex' },
                  },
                  '& .MuiTablePagination-toolbar': {
                    justifyContent: 'flex-end', 
                    overflowX: 'hidden',
                    flexWrap: 'nowrap',     
                  },
                  '& .MuiTablePagination-spacer': {
                    display: 'none',
                  },
                  '& ::-webkit-scrollbar': {
                    display: 'none',
                  },
                  scrollbarWidth: 'none',
                }}
              />
            </>
          )}
        </Paper>
      </Container>

      {selectedReport?.targetType === 'community' ? (
        <AdminCommunityReportsActionModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedReport(null);
          }}
          report={selectedReport}
          onDeleteCommunity={handleDeleteCommunity}
          onUpdateStatus={handleStatusUpdate}
          isDark={isDark}
        />
      ) : (
        <AdminReportsActionModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedReport(null);
          }}
          report={selectedReport}
          onDeletePost={handleDeletePost}
          onUpdateStatus={handleStatusUpdate}
          isDark={isDark}
        />
      )}
    </Box>
  );
};

export default AdminReports;